import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTreatXAdminAuth } from '../../context/TreatXAdminAuthContext';
import { useAdminAnalytics, useTopClinics, useFinanceHeartbeat, useAttribution, useCampaignROI, useForecast, useOfferAnalytics, useResponseSpeed, useMessagingFunnel } from '../../hooks/useAdminAnalytics';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Cell, CartesianGrid
} from 'recharts';
import {
  ArrowLeft, RefreshCw, Calendar as CalIcon,
  TrendingUp, Users, FileText, ShoppingCart,
  DollarSign, Shield, AlertTriangle, ArrowRight,
  Activity, ChevronDown, Building2, Trophy, Percent,
  CheckCircle2, XCircle, Clock, List, Loader2, MessageCircle, Phone, CreditCard
} from 'lucide-react';
import CampaignAttributionWidget from '../../components/analytics/CampaignAttributionWidget';
import CampaignROIWidget from '../../components/analytics/CampaignROIWidget';
import ForecastOverlayWidget from '../../components/analytics/ForecastOverlayWidget';

const fmt = (v) => {
  if (v == null) return '-';
  if (typeof v === 'number') return v.toLocaleString();
  return String(v);
};
const fmtMicros = (v) => v != null ? `$${(v / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-';

function getDefaultWindow() {
  const now = new Date();
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  return { from, to: now };
}

export default function AdminAnalyticsDashboard() {
  const { admin } = useTreatXAdminAuth();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState(getDefaultWindow);
  const [clinicMetric, setClinicMetric] = useState('commission_net');
  const [attrGroupBy, setAttrGroupBy] = useState('utm_campaign');

  const fromIso = useMemo(() => dateRange.from?.toISOString(), [dateRange.from]);
  const toIso = useMemo(() => dateRange.to?.toISOString(), [dateRange.to]);

  const { data, loading, error, refetch } = useAdminAnalytics(fromIso, toIso);
  const { clinics, loading: clinicsLoading } = useTopClinics(fromIso, toIso, clinicMetric, 10);
  const { data: heartbeat, loading: hbLoading } = useFinanceHeartbeat(fromIso, toIso, 30000);
  const { data: attrData, loading: attrLoading, error: attrError, refetch: attrRefetch } = useAttribution(fromIso, toIso, attrGroupBy);
  const { data: roiData, loading: roiLoading, error: roiError } = useCampaignROI(fromIso, toIso);
  const { data: forecastData, loading: forecastLoading, error: forecastError } = useForecast(30);
  const { data: offerData, loading: offerLoading } = useOfferAnalytics(fromIso, toIso);
  const { data: responseData, loading: responseLoading } = useResponseSpeed();
  const { data: funnelMsgData, loading: funnelMsgLoading } = useMessagingFunnel(fromIso, toIso);

  const handleDateSelect = useCallback((range) => {
    if (range?.from) setDateRange({ from: range.from, to: range.to || range.from });
  }, []);

  if (!admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Card className="w-96">
          <CardContent className="p-6 text-center">
            <Shield className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500 mb-3">Admin login required</p>
            <Button onClick={() => navigate('/login')} data-testid="login-redirect">Sign In</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const funnel = data?.funnel;
  const revenue = data?.revenue;
  const ledger = revenue?.detail?.ledger;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-analytics-dashboard">
      <header className="bg-[#122033] text-white border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10"
              onClick={() => navigate('/treatx-admin/dashboard')} data-testid="back-to-admin">
              <ArrowLeft className="h-4 w-4 mr-1" /> Admin
            </Button>
            <div className="h-5 w-px bg-white/20" />
            <h1 className="text-lg font-semibold tracking-tight font-[Manrope]">Platform Analytics</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10"
              onClick={() => navigate('/treatx-admin/verification-center')} data-testid="go-to-verification">
              <Shield className="h-4 w-4 mr-1" /> Verification Center
            </Button>
            <DateRangePicker value={dateRange} onChange={handleDateSelect} />
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10"
              onClick={refetch} disabled={loading} data-testid="refresh-analytics">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {error && <ErrorBanner message={error} onRetry={refetch} />}

        {/* Financial Heartbeat */}
        <HeartbeatWidget heartbeat={heartbeat} loading={hbLoading} ledger={ledger} revenue={revenue} />

        {/* KPI Tiles */}
        <section data-testid="kpi-section">
          <h2 className="text-base font-semibold text-[#122033] mb-3 font-[Manrope]">Key Metrics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            <KpiCard label="New Leads" value={fmt(funnel?.new_leads_count)} icon={Users} color="#3BA4C5" loading={loading} testId="kpi-leads" />
            <KpiCard label="Active Threads" value={fmt(funnel?.active_threads_count)} icon={FileText} color="#6366F1" loading={loading} testId="kpi-threads" />
            <KpiCard label="Quotes" value={fmt(funnel?.quotes_created_count)} icon={FileText} color="#F59E0B" loading={loading} testId="kpi-quotes" />
            <KpiCard label="Bookings" value={fmt(funnel?.bookings_created_count)} icon={ShoppingCart} color="#10B981" loading={loading} testId="kpi-bookings" />
            <KpiCard label="Lead-to-Booking" value={funnel ? `${funnel.conversion.lead_to_booking_pct}%` : '-'} icon={Percent} color="#8B5CF6" loading={loading} testId="kpi-conversion" />
          </div>
        </section>

        {/* Revenue KPIs */}
        <section data-testid="revenue-kpi-section">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-base font-semibold text-[#122033] font-[Manrope]">Revenue</h2>
            {revenue === null && <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">PG Unavailable</Badge>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            <KpiCard label="Commission Net" value={fmtMicros(revenue?.total_commission_net_micros)} icon={DollarSign} color="#10B981" loading={loading} testId="kpi-commission" />
            <KpiCard label="VAT Collected" value={fmtMicros(revenue?.total_vat_micros)} icon={DollarSign} color="#F59E0B" loading={loading} testId="kpi-vat" />
            <KpiCard label="Commission Gross" value={fmtMicros(revenue?.total_commission_gross_micros)} icon={TrendingUp} color="#3BA4C5" loading={loading} testId="kpi-gross" />
            <KpiCard label="Escrow Funded" value={fmtMicros(revenue?.escrow_funded_net_micros)} icon={Shield} color="#6366F1" loading={loading} testId="kpi-escrow" />
            {/* UI-C · legacy "Withdrawals Paid" KPI retired. */}
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Funnel Chart */}
          <FunnelChart funnel={funnel} loading={loading} />
          {/* Revenue Placeholder / Totals */}
          <RevenueSummary revenue={revenue} loading={loading} />
        </div>

        {/* Top Clinics */}
        <TopClinicsTable clinics={clinics} loading={clinicsLoading} metric={clinicMetric} onMetricChange={setClinicMetric} />

        {/* Campaign Attribution (Phase 3.1) */}
        <CampaignAttributionWidget
          data={attrData}
          loading={attrLoading}
          error={attrError}
          groupBy={attrGroupBy}
          onGroupByChange={setAttrGroupBy}
          onRetry={attrRefetch}
        />

        {/* Campaign ROI (Growth Visibility Layer) */}
        <CampaignROIWidget data={roiData} loading={roiLoading} error={roiError} />

        {/* Revenue Forecast Overlay */}
        <ForecastOverlayWidget
          forecast={forecastData}
          loading={forecastLoading}
          error={forecastError}
          activeCampaignCount={roiData?.campaigns?.filter(c => c.status === 'active').length || 0}
        />

        {/* Offer / Quote Analytics */}
        <OfferAnalyticsSection data={offerData} loading={offerLoading} />

        {/* Clinic Response Speed */}
        <ResponseSpeedSection data={responseData} loading={responseLoading} />

        {/* Messaging Funnel Analytics */}
        <MessagingFunnelSection data={funnelMsgData} loading={funnelMsgLoading} />
      </main>
    </div>
  );
}

/* ─── Date Range Picker ───────────────────────────────────── */
function DateRangePicker({ value, onChange }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="text-white/80 hover:text-white hover:bg-white/10 text-xs gap-1" data-testid="date-range-picker">
          <CalIcon className="h-3.5 w-3.5" />
          {value.from?.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          {value.to ? ` - ${value.to.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}` : ''}
          <ChevronDown className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <Calendar mode="range" selected={value} onSelect={onChange}
          numberOfMonths={1} defaultMonth={value.from} />
      </PopoverContent>
    </Popover>
  );
}

/* ─── Error Banner ────────────────────────────────────────── */
function ErrorBanner({ message, onRetry }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center justify-between" data-testid="analytics-error">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-red-500" />
        <span className="text-sm text-red-700">{message}</span>
      </div>
      <Button size="sm" variant="outline" onClick={onRetry} data-testid="retry-analytics">Retry</Button>
    </div>
  );
}

/* ─── KPI Card ────────────────────────────────────────────── */
function KpiCard({ label, value, icon: Icon, color, loading, testId }) {
  return (
    <Card className="border border-slate-200/60 shadow-sm hover:shadow-md transition-shadow" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-slate-500 font-medium font-[DM_Sans]">{label}</span>
          <Icon className="h-3.5 w-3.5" style={{ color }} />
        </div>
        {loading ? (
          <div className="h-7 w-24 bg-slate-100 animate-pulse rounded" />
        ) : (
          <p className="text-xl font-bold text-[#122033] tracking-tight font-[Manrope]">{value}</p>
        )}
      </CardContent>
    </Card>
  );
}

/* ─── Financial Heartbeat Widget ──────────────────────────── */
function HeartbeatWidget({ heartbeat, loading, ledger, revenue }) {
  const esc = heartbeat?.escrow || revenue?.detail?.escrow;
  // UI-C · legacy Provider-withdrawal-request telemetry retired.
  // Settlement / reversal monitoring lives in /treatx-admin/finance.
  const isBalanced = ledger?.balanced;

  return (
    <Card className="border-l-4 shadow-sm" style={{ borderLeftColor: isBalanced === false ? '#EF4444' : '#10B981' }} data-testid="heartbeat-widget">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-[#3BA4C5]" />
            <span className="text-sm font-semibold text-[#122033] font-[Manrope]">Financial Heartbeat</span>
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-slate-400 border-slate-200">auto-refresh 30s</Badge>
          </div>
          {isBalanced != null && (
            <Badge variant={isBalanced ? 'default' : 'destructive'} className={isBalanced ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''} data-testid="ledger-balanced-badge">
              {isBalanced ? 'Ledger Balanced' : 'IMBALANCED'}
            </Badge>
          )}
        </div>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[1,2,3,4].map(i => <div key={i} className="h-12 bg-slate-50 animate-pulse rounded" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <HbStat label="Escrow Funded" value={fmtMicros(esc?.funded_micros || esc?.net_funded_micros)} testId="hb-escrow-funded" />
            <HbStat label="Escrow Available" value={fmtMicros(esc?.net_funded_micros)} testId="hb-escrow-available" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function HbStat({ label, value, testId }) {
  return (
    <div className="bg-slate-50 rounded-md px-3 py-2" data-testid={testId}>
      <p className="text-[10px] text-slate-400 uppercase tracking-wider">{label}</p>
      <p className="text-sm font-semibold text-[#122033] font-[Manrope]">{value}</p>
    </div>
  );
}

/* ─── Funnel Bar Chart ────────────────────────────────────── */
const FUNNEL_COLORS = ['#3BA4C5', '#6366F1', '#F59E0B', '#10B981'];

function FunnelChart({ funnel, loading }) {
  const chartData = useMemo(() => {
    if (!funnel) return [];
    return [
      { name: 'Leads', value: funnel.new_leads_count, fill: FUNNEL_COLORS[0] },
      { name: 'Quotes', value: funnel.quotes_created_count, fill: FUNNEL_COLORS[2] },
      { name: 'Accepted', value: funnel.quotes_accepted_count, fill: FUNNEL_COLORS[1] },
      { name: 'Bookings', value: funnel.bookings_created_count, fill: FUNNEL_COLORS[3] },
    ];
  }, [funnel]);

  const convLabels = useMemo(() => {
    if (!funnel) return [];
    const c = funnel.conversion;
    return [
      { from: 'Leads', to: 'Quotes', pct: c.lead_to_quote_pct },
      { from: 'Quotes', to: 'Bookings', pct: c.quote_to_booking_pct },
      { from: 'Leads', to: 'Bookings', pct: c.lead_to_booking_pct },
    ];
  }, [funnel]);

  return (
    <Card className="border border-slate-200/60 shadow-sm" data-testid="funnel-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope]">Conversion Funnel</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-48 bg-slate-50 animate-pulse rounded" />
        ) : chartData.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} barSize={40}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748B' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => [fmt(v), 'Count']} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {chartData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="flex gap-3 mt-3 flex-wrap" data-testid="conversion-rates">
              {convLabels.map((c, i) => (
                <div key={i} className="flex items-center gap-1 text-xs text-slate-500">
                  <span>{c.from}</span>
                  <ArrowRight className="h-3 w-3" />
                  <span>{c.to}</span>
                  <Badge variant="outline" className="text-[10px] px-1 py-0 ml-0.5">{c.pct}%</Badge>
                </div>
              ))}
            </div>
          </>
        ) : (
          <EmptyState msg="No funnel data for this window" />
        )}
      </CardContent>
    </Card>
  );
}

/* ─── Revenue Summary Card ────────────────────────────────── */
function RevenueSummary({ revenue, loading }) {
  if (loading) {
    return (
      <Card className="border border-slate-200/60 shadow-sm" data-testid="revenue-summary">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope]">Revenue Breakdown</CardTitle></CardHeader>
        <CardContent><div className="h-48 bg-slate-50 animate-pulse rounded" /></CardContent>
      </Card>
    );
  }

  if (!revenue || revenue.error) {
    return (
      <Card className="border border-slate-200/60 shadow-sm" data-testid="revenue-summary">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope]">Revenue Breakdown</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-amber-600 bg-amber-50 rounded-md px-3 py-6 justify-center" data-testid="revenue-unavailable">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm">Revenue data unavailable</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const detail = revenue.detail;
  const ms = detail?.milestones;
  const inv = detail?.invoices;

  const rows = [
    { label: 'Released Journey Steps', value: `${ms?.released || 0} of ${ms?.total || 0}`, sub: fmtMicros(ms?.released_micros) },
    { label: 'Commission Earned', value: fmtMicros(ms?.commission_earned_micros), sub: null },
    { label: 'VAT Collected', value: fmtMicros(ms?.vat_collected_micros), sub: null },
    { label: 'Pending Journey Steps', value: fmt(ms?.pending), sub: null },
    { label: 'Invoices Issued', value: fmt(inv?.total), sub: fmtMicros(inv?.issued_gross_micros) },
    { label: 'Invoices Voided', value: fmt(inv?.voided), sub: null },
  ];

  return (
    <Card className="border border-slate-200/60 shadow-sm" data-testid="revenue-summary">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope]">Revenue Breakdown</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2.5">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
              <span className="text-xs text-slate-500">{r.label}</span>
              <div className="text-right">
                <span className="text-sm font-semibold text-[#122033] font-[Manrope]">{r.value}</span>
                {r.sub && <span className="text-[10px] text-slate-400 ml-1.5">{r.sub}</span>}
              </div>
            </div>
          ))}
        </div>
        {/* Revenue over time placeholder */}
        <div className="mt-4 bg-slate-50 rounded-md p-4 text-center" data-testid="revenue-chart-placeholder">
          <TrendingUp className="h-5 w-5 text-slate-300 mx-auto mb-1" />
          <p className="text-[10px] text-slate-400 uppercase tracking-wider">Daily revenue chart</p>
          <p className="text-[10px] text-slate-300">Available when backend provides daily buckets</p>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── Top Clinics Leaderboard ─────────────────────────────── */
function TopClinicsTable({ clinics, loading, metric, onMetricChange }) {
  const metrics = [
    { value: 'commission_net', label: 'Commission' },
    { value: 'released_volume', label: 'Volume' },
    { value: 'milestone_count', label: 'Journey Steps' },
  ];

  return (
    <Card className="border border-slate-200/60 shadow-sm" data-testid="top-clinics-table">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope] flex items-center gap-2">
            <Trophy className="h-4 w-4 text-amber-500" /> Top Clinics
          </CardTitle>
          <div className="flex gap-1">
            {metrics.map(m => (
              <Button key={m.value} size="sm" variant={metric === m.value ? 'default' : 'outline'}
                className={`text-xs h-7 px-2 ${metric === m.value ? 'bg-[#122033] text-white' : 'text-slate-500'}`}
                onClick={() => onMetricChange(m.value)} data-testid={`metric-${m.value}`}>
                {m.label}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-10 bg-slate-50 animate-pulse rounded" />)}</div>
        ) : clinics.length === 0 ? (
          <EmptyState msg="No clinic data for this window" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="clinics-leaderboard">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-2 text-[10px] uppercase text-slate-400 font-medium">#</th>
                  <th className="text-left py-2 text-[10px] uppercase text-slate-400 font-medium">Clinic ID</th>
                  <th className="text-right py-2 text-[10px] uppercase text-slate-400 font-medium">Commission</th>
                  <th className="text-right py-2 text-[10px] uppercase text-slate-400 font-medium">Volume</th>
                  <th className="text-right py-2 text-[10px] uppercase text-slate-400 font-medium">Milestones</th>
                </tr>
              </thead>
              <tbody>
                {clinics.map((c, i) => (
                  <tr key={c.clinic_id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`clinic-row-${i}`}>
                    <td className="py-2 text-xs text-slate-400 font-medium">{i + 1}</td>
                    <td className="py-2">
                      <div className="flex items-center gap-1.5">
                        <Building2 className="h-3.5 w-3.5 text-slate-400" />
                        <span className="text-xs font-medium text-[#122033] font-mono truncate max-w-[200px]">{c.clinic_id}</span>
                        {c._demo && <Badge className="bg-amber-100 text-amber-700 text-[9px] px-1 py-0" data-testid={`demo-badge-${i}`}>Demo</Badge>}
                      </div>
                    </td>
                    <td className="py-2 text-right text-xs font-semibold text-emerald-600">{fmtMicros(c.commission_micros)}</td>
                    <td className="py-2 text-right text-xs text-slate-600">{fmtMicros(c.volume_micros)}</td>
                    <td className="py-2 text-right text-xs text-slate-600">{c.milestone_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ─── Offer / Quote Analytics Section ─────────────────────── */
function OfferAnalyticsSection({ data, loading }) {
  if (loading) {
    return (
      <Card className="border border-slate-200/60 shadow-sm" data-testid="offer-analytics-loading">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope] flex items-center gap-2">
            <FileText className="h-4 w-4 text-blue-500" /> Offer Analytics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-slate-50 animate-pulse rounded" />)}</div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card className="border border-slate-200/60 shadow-sm" data-testid="offer-analytics-empty">
        <CardContent className="py-8"><EmptyState msg="No offer data available" /></CardContent>
      </Card>
    );
  }

  const t = data.totals || {};
  const r = data.rates || {};
  const spd = data.speed || {};
  const perDay = data.per_day || [];
  const byClinic = data.by_clinic || [];
  const byTreatment = data.by_treatment || [];

  return (
    <section className="space-y-4" data-testid="offer-analytics-section">
      <h2 className="text-base font-semibold text-[#122033] font-[Manrope] flex items-center gap-2">
        <FileText className="h-4 w-4 text-blue-500" /> Offer Conversion Analytics
      </h2>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="offer-kpi-grid">
        <KpiCard label="Total Sent" value={fmt(t.sent)} icon={FileText} color="#3BA4C5" testId="offer-kpi-sent" />
        <KpiCard label="Accepted" value={fmt(t.accepted)} icon={CheckCircle2} color="#10B981" testId="offer-kpi-accepted" />
        <KpiCard label="Rejected" value={fmt(t.rejected)} icon={XCircle} color="#EF4444" testId="offer-kpi-rejected" />
        <KpiCard label="Pending" value={fmt(t.pending)} icon={Clock} color="#F59E0B" testId="offer-kpi-pending" />
        <KpiCard label="Accept Rate" value={`${r.acceptance_pct ?? 0}%`} icon={Percent} color="#8B5CF6" testId="offer-kpi-accept-rate" />
        <KpiCard label="Structured" value={fmt(t.with_offer_items)} icon={List} color="#6366F1" testId="offer-kpi-structured" />
      </div>

      {/* Speed + Chart row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Speed card */}
        <Card className="border border-slate-200/60 shadow-sm" data-testid="offer-speed-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope]">Decision Speed</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-xs text-slate-500">Median time to any decision</span>
              <span className="text-sm font-bold text-[#122033]" data-testid="offer-median-decision">
                {spd.median_decision_hours != null ? `${spd.median_decision_hours}h` : '—'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-slate-500">Median time to accept</span>
              <span className="text-sm font-bold text-emerald-600" data-testid="offer-median-accept">
                {spd.median_accept_hours != null ? `${spd.median_accept_hours}h` : '—'}
              </span>
            </div>
            <div className="h-px bg-slate-100" />
            <div className="flex justify-between items-center">
              <span className="text-xs text-slate-500">Expired</span>
              <span className="text-sm font-medium text-red-500" data-testid="offer-expired-count">{fmt(t.expired)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Per-day chart (bar table) */}
        <Card className="border border-slate-200/60 shadow-sm" data-testid="offer-per-day-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope]">Daily Volume</CardTitle>
          </CardHeader>
          <CardContent>
            {perDay.length === 0 ? (
              <EmptyState msg="No daily data" />
            ) : (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {perDay.map(d => {
                  const maxVal = Math.max(...perDay.map(p => p.sent), 1);
                  const pct = Math.round((d.sent / maxVal) * 100);
                  return (
                    <div key={d.date} className="flex items-center gap-2 text-xs" data-testid={`day-row-${d.date}`}>
                      <span className="w-16 text-slate-400 shrink-0 font-mono text-[10px]">{d.date.slice(5)}</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-4 relative overflow-hidden">
                        <div className="absolute inset-y-0 left-0 bg-blue-400 rounded-full" style={{ width: `${pct}%` }} />
                        {d.accepted > 0 && (
                          <div className="absolute inset-y-0 left-0 bg-emerald-400 rounded-full" style={{ width: `${Math.round((d.accepted / maxVal) * 100)}%` }} />
                        )}
                      </div>
                      <span className="w-8 text-right font-medium text-slate-600">{d.sent}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Breakdown tables row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* By Clinic */}
        <Card className="border border-slate-200/60 shadow-sm" data-testid="offer-by-clinic">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope] flex items-center gap-2">
              <Building2 className="h-4 w-4 text-slate-400" /> By Clinic (Top 10)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {byClinic.length === 0 ? <EmptyState msg="No clinic data" /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="offer-clinic-table">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-1.5 text-[10px] uppercase text-slate-400 font-medium">Clinic</th>
                      <th className="text-right py-1.5 text-[10px] uppercase text-slate-400 font-medium">Sent</th>
                      <th className="text-right py-1.5 text-[10px] uppercase text-slate-400 font-medium">Acc.</th>
                      <th className="text-right py-1.5 text-[10px] uppercase text-slate-400 font-medium">Rej.</th>
                      <th className="text-right py-1.5 text-[10px] uppercase text-slate-400 font-medium">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byClinic.map((c, i) => (
                      <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`clinic-offer-row-${i}`}>
                        <td className="py-1.5 font-medium text-[#122033] truncate max-w-[150px]">{c.clinic_name}</td>
                        <td className="py-1.5 text-right text-slate-600">{c.total}</td>
                        <td className="py-1.5 text-right text-emerald-600 font-medium">{c.accepted}</td>
                        <td className="py-1.5 text-right text-red-500">{c.rejected}</td>
                        <td className="py-1.5 text-right font-semibold text-[#122033]">{c.accept_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* By Treatment */}
        <Card className="border border-slate-200/60 shadow-sm" data-testid="offer-by-treatment">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope]">By Treatment (Top 10)</CardTitle>
          </CardHeader>
          <CardContent>
            {byTreatment.length === 0 ? <EmptyState msg="No treatment data" /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="offer-treatment-table">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-1.5 text-[10px] uppercase text-slate-400 font-medium">Treatment</th>
                      <th className="text-right py-1.5 text-[10px] uppercase text-slate-400 font-medium">Sent</th>
                      <th className="text-right py-1.5 text-[10px] uppercase text-slate-400 font-medium">Acc.</th>
                      <th className="text-right py-1.5 text-[10px] uppercase text-slate-400 font-medium">Rej.</th>
                      <th className="text-right py-1.5 text-[10px] uppercase text-slate-400 font-medium">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byTreatment.map((t, i) => (
                      <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`treatment-offer-row-${i}`}>
                        <td className="py-1.5 font-medium text-[#122033] truncate max-w-[150px]">{t.treatment}</td>
                        <td className="py-1.5 text-right text-slate-600">{t.total}</td>
                        <td className="py-1.5 text-right text-emerald-600 font-medium">{t.accepted}</td>
                        <td className="py-1.5 text-right text-red-500">{t.rejected}</td>
                        <td className="py-1.5 text-right font-semibold text-[#122033]">{t.accept_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

function ResponseSpeedSection({ data, loading }) {
  if (loading) {
    return (
      <Card className="border border-slate-200/60 shadow-sm" data-testid="response-speed-loading">
        <CardContent className="py-8"><div className="flex items-center justify-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading response metrics...</div></CardContent>
      </Card>
    );
  }

  if (!data || !data.platform) {
    return (
      <Card className="border border-slate-200/60 shadow-sm" data-testid="response-speed-empty">
        <CardContent className="py-8"><EmptyState msg="No response speed data available" /></CardContent>
      </Card>
    );
  }

  const p = data.platform;
  const clinics = data.clinics || [];

  return (
    <section className="space-y-4" data-testid="response-speed-section">
      <h2 className="text-base font-semibold text-[#122033] font-[Manrope] flex items-center gap-2">
        <Activity className="h-4 w-4 text-emerald-500" /> Clinic Response Speed
      </h2>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="response-speed-kpi-grid">
        <KpiCard label="Avg Response" value={p.avg_response_hours != null ? `${p.avg_response_hours}h` : '-'} icon={Clock} color="#3BA4C5" testId="response-kpi-avg" />
        <KpiCard label="Fastest Clinic" value={p.fastest_hours != null ? `${p.fastest_hours}h` : '-'} icon={TrendingUp} color="#10B981" testId="response-kpi-fastest" />
        <KpiCard label="Slowest Clinic" value={p.slowest_hours != null ? `${p.slowest_hours}h` : '-'} icon={AlertTriangle} color="#EF4444" testId="response-kpi-slowest" />
        <KpiCard label="Clinics Tracked" value={fmt(p.clinic_count)} icon={Building2} color="#6366F1" testId="response-kpi-count" />
      </div>

      {p.fastest_clinic_name && (
        <div className="flex flex-wrap gap-3 text-xs" data-testid="response-speed-highlights">
          <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50">
            Fastest: {p.fastest_clinic_name} ({p.fastest_hours}h)
          </Badge>
          <Badge variant="outline" className="text-red-700 border-red-200 bg-red-50">
            Slowest: {p.slowest_clinic_name} ({p.slowest_hours}h)
          </Badge>
        </div>
      )}

      {clinics.length > 0 && (
        <Card className="border border-slate-200/60 shadow-sm">
          <CardContent className="p-4">
            <h3 className="text-xs font-semibold text-slate-600 mb-2">Per-Clinic Breakdown</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="response-speed-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-1.5 text-[10px] uppercase text-slate-400 font-medium">Clinic</th>
                    <th className="text-right py-1.5 text-[10px] uppercase text-slate-400 font-medium">Median (h)</th>
                    <th className="text-right py-1.5 text-[10px] uppercase text-slate-400 font-medium">Replies</th>
                  </tr>
                </thead>
                <tbody>
                  {clinics.map((c, i) => (
                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`response-clinic-row-${i}`}>
                      <td className="py-1.5 font-medium text-[#122033] truncate max-w-[200px]">{c.clinic_name || c.clinic_id}</td>
                      <td className="py-1.5 text-right text-slate-600">{c.median_first_reply_hours != null ? c.median_first_reply_hours : '-'}</td>
                      <td className="py-1.5 text-right text-slate-500">{(c.first_reply_times_hours || []).length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </section>
  );
}

function MessagingFunnelSection({ data, loading }) {
  if (loading) {
    return (
      <Card className="border border-slate-200/60 shadow-sm" data-testid="messaging-funnel-loading">
        <CardContent className="py-8"><div className="flex items-center justify-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading messaging funnel...</div></CardContent>
      </Card>
    );
  }

  if (!data || !data.global_funnel) {
    return (
      <Card className="border border-slate-200/60 shadow-sm" data-testid="messaging-funnel-empty">
        <CardContent className="py-8"><EmptyState msg="No messaging funnel data available" /></CardContent>
      </Card>
    );
  }

  const g = data.global_funnel;
  const s = data.speed_metrics || {};
  const clinics = data.clinic_breakdown || [];
  const camp = data.campaign_analytics || {};
  const pr = data.payment_request_analytics || {};

  const funnelSteps = [
    { label: 'Offer Threads', value: g.total_offer_threads, color: '#6366F1' },
    { label: 'Patient Messages', value: g.threads_with_patient_message, color: '#3BA4C5' },
    { label: 'Clinic Replies', value: g.threads_with_clinic_reply, color: '#10B981' },
    { label: 'WhatsApp Unlocked', value: g.threads_with_whatsapp_unlock, color: '#F59E0B' },
    { label: 'Payment Requested', value: pr.payment_requested_count || 0, color: '#EC4899' },
    { label: 'Offer Accepted', value: g.threads_with_accepted_offer, color: '#8B5CF6' },
    { label: 'Payment Completed', value: g.threads_with_payment_completed, color: '#059669' },
  ];
  const maxVal = Math.max(...funnelSteps.map(f => f.value), 1);

  return (
    <section className="space-y-4" data-testid="messaging-funnel-section">
      <h2 className="text-base font-semibold text-[#122033] font-[Manrope] flex items-center gap-2">
        <MessageCircle className="h-4 w-4 text-[#3BA4C5]" /> Messaging Funnel Analytics
      </h2>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="messaging-funnel-kpi-grid">
        <KpiCard label="Offer → Message" value={`${g.offer_to_message_rate}%`} icon={MessageCircle} color="#3BA4C5" testId="funnel-kpi-offer-msg" />
        <KpiCard label="Msg → WhatsApp" value={`${g.message_to_whatsapp_unlock_rate}%`} icon={Phone} color="#F59E0B" testId="funnel-kpi-msg-wa" />
        <KpiCard label="Msg → Payment" value={`${g.message_to_payment_rate}%`} icon={DollarSign} color="#059669" testId="funnel-kpi-msg-pay" />
        <KpiCard label="Payment Requests" value={pr.payment_requested_count || 0} icon={CreditCard} color="#EC4899" testId="funnel-kpi-pay-req" />
        <KpiCard label="Req → Paid" value={`${pr.payment_requested_to_payment_rate || 0}%`} icon={DollarSign} color="#059669" testId="funnel-kpi-req-paid" />
        <KpiCard label="Avg Reply Time" value={s.avg_first_clinic_reply_minutes != null ? `${s.avg_first_clinic_reply_minutes}m` : '-'} icon={Clock} color="#6366F1" testId="funnel-kpi-reply" />
      </div>

      {/* Funnel Visualization */}
      <Card className="border border-slate-200/60 shadow-sm" data-testid="messaging-funnel-chart">
        <CardContent className="p-4">
          <h3 className="text-xs font-semibold text-slate-600 mb-3">Conversion Funnel</h3>
          <div className="space-y-2">
            {funnelSteps.map((step, i) => {
              const pct = maxVal > 0 ? (step.value / maxVal) * 100 : 0;
              const convRate = i > 0 && funnelSteps[i - 1].value > 0
                ? ((step.value / funnelSteps[i - 1].value) * 100).toFixed(1)
                : null;
              return (
                <div key={step.label} className="flex items-center gap-3" data-testid={`funnel-step-${i}`}>
                  <span className="w-28 text-[11px] text-slate-500 text-right shrink-0">{step.label}</span>
                  <div className="flex-1 h-6 bg-slate-50 rounded overflow-hidden relative">
                    <div
                      className="h-full rounded transition-all duration-700"
                      style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: step.color }}
                    />
                    <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-slate-700">
                      {step.value}
                    </span>
                  </div>
                  {convRate && (
                    <span className="text-[10px] text-slate-400 w-12 shrink-0">{convRate}%</span>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Clinic Performance Table */}
      {clinics.length > 0 && (
        <Card className="border border-slate-200/60 shadow-sm" data-testid="messaging-clinic-table-card">

      {/* Campaign Analytics */}
      {(camp.campaign_used_in_offers > 0 || camp.campaign_offer_acceptance_rate > 0) && (
        <Card className="border border-slate-200/60 shadow-sm" data-testid="campaign-analytics-card">
          <CardContent className="p-4">
            <h3 className="text-xs font-semibold text-slate-600 mb-3">Campaign Package Performance</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-rose-50 rounded-lg" data-testid="campaign-used-count">
                <p className="text-xl font-bold text-rose-700">{camp.campaign_used_in_offers}</p>
                <p className="text-[10px] text-slate-500">Campaigns Used in Offers</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg" data-testid="campaign-accept-rate">
                <p className="text-xl font-bold text-green-700">{camp.campaign_offer_acceptance_rate}%</p>
                <p className="text-[10px] text-slate-500">Campaign Acceptance Rate</p>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg" data-testid="campaign-pay-rate">
                <p className="text-xl font-bold text-blue-700">{camp.campaign_offer_payment_rate}%</p>
                <p className="text-[10px] text-slate-500">Campaign Payment Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
          <CardContent className="p-4">
            <h3 className="text-xs font-semibold text-slate-600 mb-2">Clinic Messaging Performance</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="messaging-clinic-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-1.5 text-[10px] uppercase text-slate-400 font-medium">Clinic</th>
                    <th className="text-right py-1.5 text-[10px] uppercase text-slate-400 font-medium">Conversations</th>
                    <th className="text-right py-1.5 text-[10px] uppercase text-slate-400 font-medium">Avg Reply (min)</th>
                    <th className="text-right py-1.5 text-[10px] uppercase text-slate-400 font-medium">WA Unlocks</th>
                    <th className="text-right py-1.5 text-[10px] uppercase text-slate-400 font-medium">Payment Conv%</th>
                    <th className="text-right py-1.5 text-[10px] uppercase text-slate-400 font-medium">Accept Rate%</th>
                  </tr>
                </thead>
                <tbody>
                  {clinics.map((c, i) => (
                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`msg-clinic-row-${i}`}>
                      <td className="py-1.5 font-medium text-[#122033] truncate max-w-[200px]">{c.clinic_name || c.clinic_id}</td>
                      <td className="py-1.5 text-right text-slate-600">{c.total_conversations}</td>
                      <td className="py-1.5 text-right text-slate-600">{c.avg_first_reply_minutes != null ? c.avg_first_reply_minutes : '-'}</td>
                      <td className="py-1.5 text-right text-slate-600">{c.whatsapp_unlock_count}</td>
                      <td className="py-1.5 text-right text-slate-600">{c.payment_conversion_rate}%</td>
                      <td className="py-1.5 text-right text-slate-600">{c.offer_acceptance_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </section>
  );
}


function EmptyState({ msg }) {
  return (
    <div className="py-8 text-center text-sm text-slate-400" data-testid="empty-state">{msg}</div>
  );
}
